// Apolo IDE — E5 (Camada V): policy declarativa de tool (por-tool / por-path).
//
// Doc "Anatomia do Claude" (5.2): "o sistema valida (permissões)" — é no harness
// que a segurança mora, não no modelo. Hoje o gate é binário (ask/ignore) +
// readOnly. Esta policy adiciona deny-list granular SOBRE eles, sem regredir:
// policy null/ausente → comportamento atual intacto.
//
// PURO: sem imports, testável sem mock. O agentLoop chama evaluateToolPolicy no
// gate (junto do readOnly) e hard-denya tools que batem a policy.
//
// policy = { deny?: string[],         // nomes de tool bloqueados
//            denyPaths?: string[] }   // substrings de path bloqueadas (em args.path/file/cwd)

// Every path-bearing arg key the tool executors ACCEPT must be here, or denyPaths
// is silently bypassed by an alias. agentTools reads path||filePath||file (read/
// write/apply_edit) and path||dir||directory||folder (list_files); norm()
// lowercases, so `filePath`→`filepath`. Missing aliases = a hole in the deny-list.
const PATH_ARG_KEYS = ['path', 'file', 'filepath', 'filename', 'cwd', 'dir', 'directory', 'folder'];

function norm(s) {
  return String(s ?? '').trim().toLowerCase();
}

// E0 (llm-harness-v2) — the DEFAULT deny-list wired at every composition root that
// runs the agent loop (SessionWorkspace, useRemoteRunner, appExecutionAdapter). Until
// now the loop accepted a policy but no caller passed one, so the layer was inert.
// Entries are SUBSTRING matches on path-args, chosen to avoid common false positives:
//   - `.git` is intentionally NOT here — it false-positives `.github/workflows`
//     (every repo has it); the `.git` WRITE vector (hook implant) is blocked precisely
//     in electron/files.cjs `writeDenied`.
//   - `.env` is omitted — legit dev reads + `*.environment` false positives.
// These are high-value secret locations that rarely appear in a legitimate path.
// Callers may pass an explicit policy to override; merge-extend via mergeToolPolicy.
export function defaultToolPolicy() {
  return { denyPaths: ['.ssh', '.aws', '.gnupg', '.config/gh', 'id_rsa', 'id_ed25519'] };
}

/**
 * Merge a caller override onto the default. An explicit `denyPaths`/`deny` REPLACES
 * the default for that key (caller is in control); pass `{}` to keep the default.
 */
export function mergeToolPolicy(override) {
  const base = defaultToolPolicy();
  if (!override || typeof override !== 'object') return base;
  return {
    denyPaths: Array.isArray(override.denyPaths) ? override.denyPaths : base.denyPaths,
    deny: Array.isArray(override.deny) ? override.deny : base.deny,
  };
}

/**
 * evaluateToolPolicy(name, args, policy) → { denied: boolean, reason?: string }
 * - deny: nome do tool na lista (case-insensitive) → bloqueado.
 * - denyPaths: qualquer arg-de-path contém (case-insensitive) uma substring
 *   da lista → bloqueado. Substring simples (sem regex) — defensivo, sem ReDoS.
 * policy ausente/inválida → never denied (comportamento atual).
 */
export function evaluateToolPolicy(name, args, policy) {
  if (!policy || typeof policy !== 'object') return { denied: false };
  const toolName = norm(name);

  const deny = Array.isArray(policy.deny) ? policy.deny.map(norm).filter(Boolean) : [];
  if (toolName && deny.includes(toolName)) {
    return { denied: true, reason: `ferramenta "${name}" bloqueada pela policy (deny)` };
  }

  const denyPaths = Array.isArray(policy.denyPaths)
    ? policy.denyPaths.map(norm).filter(Boolean)
    : [];
  if (denyPaths.length && args && typeof args === 'object') {
    // Match path-bearing args case-INSENSITIVELY on the KEY: the model emits
    // `filePath`/`Directory`/etc, so indexing args['filepath'] literally would miss
    // the camelCase alias and silently skip the deny. Normalize each actual key.
    const pathKeySet = new Set(PATH_ARG_KEYS); // already lowercase
    for (const key of Object.keys(args)) {
      if (!pathKeySet.has(norm(key))) continue;
      const val = norm(args[key]);
      if (!val) continue;
      const hit = denyPaths.find((p) => val.includes(p));
      if (hit) {
        return { denied: true, reason: `caminho bloqueado pela policy (denyPaths: "${hit}")` };
      }
    }
  }
  return { denied: false };
}
